import React, { useState, useEffect } from 'react';
import { Typography, Alert, Table, Button, Card, Row, Col, Statistic, Tag } from 'antd';

const { Title, Text, Paragraph } = Typography;

interface ProdigyResult {
  used_selection: string;
  binding_affinity: number;
  dissociation_constant: string;
  intermolecular_contacts: number;
  charged_charged_contacts?: number;
  charged_polar_contacts?: number;
  charged_apolar_contacts?: number;
  polar_polar_contacts?: number;
  polar_apolar_contacts?: number;
  apolar_apolar_contacts?: number;
  [key: string]: any;
}

interface ProdigyResultProps {
  taskResult: any;
  data?: any;
  title?: string;
  showTitle?: boolean;
}

const ProdigyResultComponent: React.FC<ProdigyResultProps> = ({ 
  taskResult, 
  data, 
  title = "PRODIGY 预测结果", 
  showTitle = true 
}) => {
  const [selectedResult, setSelectedResult] = useState<ProdigyResult | null>(null);

  // 提取结果数据
  const getResults = (): ProdigyResult[] => {
    let results: ProdigyResult[] = [];

    // 格式1: Profile页面格式 - taskResult.result.results[].result
    if (taskResult?.result?.results && Array.isArray(taskResult.result.results)) {
      taskResult.result.results.forEach((item: any) => {
        if (item.result && typeof item.result === 'object') {
          results.push(item.result);
        }
      });
    }
    // 格式2: 任务管理器返回格式 - taskResult.results[].result
    else if (taskResult?.results && Array.isArray(taskResult.results)) {
      taskResult.results.forEach((item: any) => {
        // 如果有result字段，提取它
        if (item.result && typeof item.result === 'object') {
          results.push(item.result);
        }
        // 否则直接使用item（已经是结果对象）
        else if (item.binding_affinity !== undefined) {
          results.push(item);
        }
      });
    }
    // 格式3: data参数
    else if (data?.results && Array.isArray(data.results)) {
      data.results.forEach((item: any) => {
        if (item.result && typeof item.result === 'object') {
          results.push(item.result);
        } else if (item.binding_affinity !== undefined) {
          results.push(item);
        }
      });
    }
    // 格式4: 单个结果
    else if (taskResult?.result && typeof taskResult.result === 'object') {
      results = [taskResult.result];
    }

    return results;
  };

  // 渲染结合亲和力评估
  const renderBindingAffinityEvaluation = (value: number) => {
    if (!value) return <Text type="secondary">N/A</Text>;
    
    let color = 'default';
    let text = '未知';
    
    if (value < -10) {
      color = 'green';
      text = '强结合';
    } else if (value < -7) {
      color = 'blue';
      text = '中等结合';
    } else if (value < -5) {
      color = 'orange';
      text = '弱结合';
    } else {
      color = 'red';
      text = '极弱结合';
    }
    
    return <Tag color={color}>{text}</Tag>;
  };

  const results = getResults();

  // taskResult 变化时（切换查看不同任务）重置选中结果，避免详情面板显示上一个任务的旧数据
  useEffect(() => {
    setSelectedResult(null);
  }, [taskResult]);

  // 默认选中第一条结果，避免再次点击进入详情
  useEffect(() => {
    if (!selectedResult && results.length > 0) {
      setSelectedResult(results[0]);
    }
  }, [results, selectedResult]);

  if (!taskResult) {
    return <Alert type="warning" message="无任务数据" />;
  }

  if (results.length === 0) {
    return (
      <Alert
        type="info"
        message="无PRODIGY预测结果"
        description="任务可能还在运行中或者结果格式不正确"
        showIcon
      />
    );
  }

  const columns = [
    {
      title: '选择',
      dataIndex: 'used_selection',
      key: 'used_selection',
      render: (text: string) => text || '默认选择'
    },
    {
      title: '结合亲和力 (kcal/mol)',
      dataIndex: 'binding_affinity',
      key: 'binding_affinity',
      sorter: (a: ProdigyResult, b: ProdigyResult) => (a.binding_affinity || 0) - (b.binding_affinity || 0),
      render: (value: number) => value ? value.toFixed(2) : 'N/A'
    },
    {
      title: '解离常数 (M)',
      dataIndex: 'dissociation_constant',
      key: 'dissociation_constant'
    },
    {
      title: '分子间接触数',
      dataIndex: 'intermolecular_contacts',
      key: 'intermolecular_contacts',
      sorter: (a: ProdigyResult, b: ProdigyResult) => (a.intermolecular_contacts || 0) - (b.intermolecular_contacts || 0)
    },
    {
      title: '评估',
      key: 'evaluation',
      dataIndex: 'binding_affinity',
      render: (value: number) => renderBindingAffinityEvaluation(value)
    }
  ];

  return (
    <div>
      {showTitle && (
        <div style={{ marginBottom: 16 }}>
          <Text strong>{title}</Text>
        </div>
      )}
      
      <Table
        columns={columns}
        dataSource={results}
        rowKey={(record, index) => index?.toString() || '0'}
        pagination={false}
        size="middle"
        onRow={(record) => ({
          onClick: () => setSelectedResult(record)
        })}
        rowClassName={(record) => (record === selectedResult ? 'ant-table-row-selected' : '')}
      />

      {selectedResult && (
        <div style={{ marginTop: 16 }}>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Statistic
                title="结合亲和力"
                value={selectedResult.binding_affinity || 0}
                suffix="kcal/mol"
                precision={2}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="解离常数"
                value={selectedResult.dissociation_constant || 'N/A'}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="分子间接触数"
                value={selectedResult.intermolecular_contacts || 0}
              />
            </Col>
          </Row>

          <Card title="接触类型分布" size="small">
            <Row gutter={16}>
              <Col span={12}>
                <Text strong>电荷-电荷接触：</Text>
                <Text style={{ marginLeft: 8 }}>{selectedResult.charged_charged_contacts || 0}</Text>
              </Col>
              <Col span={12}>
                <Text strong>电荷-极性接触：</Text>
                <Text style={{ marginLeft: 8 }}>{selectedResult.charged_polar_contacts || 0}</Text>
              </Col>
              <Col span={12}>
                <Text strong>电荷-疏水接触：</Text>
                <Text style={{ marginLeft: 8 }}>{selectedResult.charged_apolar_contacts || 0}</Text>
              </Col>
              <Col span={12}>
                <Text strong>极性-极性接触：</Text>
                <Text style={{ marginLeft: 8 }}>{selectedResult.polar_polar_contacts || 0}</Text>
              </Col>
              <Col span={12}>
                <Text strong>极性-疏水接触：</Text>
                <Text style={{ marginLeft: 8 }}>{selectedResult.polar_apolar_contacts || 0}</Text>
              </Col>
              <Col span={12}>
                <Text strong>疏水-疏水接触：</Text>
                <Text style={{ marginLeft: 8 }}>{selectedResult.apolar_apolar_contacts || 0}</Text>
              </Col>
            </Row>
          </Card>

          <div style={{ marginTop: 16 }}>
            <Text strong>评估：</Text>
            {renderBindingAffinityEvaluation(selectedResult.binding_affinity)}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProdigyResultComponent;
